import { json, redirect } from "@remix-run/node"
import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/node"
import { useLoaderData } from "@remix-run/react"
import classNames from "classnames"
import type { ComponentType, FC } from "react"
import { lazy, Suspense } from "react"
import Banner from "~/components/Banner"
import DefaultErrorBoundary from "~/components/DefaultErrorBoundary"
import type { CapsuleProps } from "~/components/capsules"
import ActivityType from "~/components/capsules/ActivityType"
import Hero from "~/components/capsules/Hero"
import MultiImage from "~/components/capsules/MultiImage"
import Text from "~/components/capsules/Text"
import {
  CapsuleLoadingFallback,
  DynamicCapsule
} from "~/components/capsules/dynamicLoader"
import type { ReferralCapsuleData } from "~/components/capsules/Referral"
import { Section } from "~/components/design-system/organisms/Section"
import {
  CapsuleDocument,
  type WithCapsuleActivityCardFragment,
  type WithCapsuleActivityCardGroupFragment,
  type WithCapsuleActivityTypeFragment
} from "~/contentful"
import {
  ActivityCardPricesDocument,
  ActivityTypesDocument,
  type WithActivityTypeFragment,
  TopBunkMembershipDocument,
  ProcessingFeeProductDocument,
  LocationBySlugDocument
} from "~/graphql"
import { contentfulImageUrl } from "~/helpers/contentful"
import type { CONTAINER_SIZES } from "~/helpers/responsiveImage"
import {
  calculateOptimalImageWidth,
  getPreloadFormat,
  calculateExactDisplaySize
} from "~/helpers/responsiveImage"
import { buildMetaTags } from "~/helpers/meta-tags"
import { resolveLocationSlugAlias } from "~/helpers/location"
import { getSearchParams } from "~/helpers/params"
import usePageTracking from "~/hooks/usePageTracking"
import { headersForCaching, headersFromLoader } from "~/loaders/headers.server"
import { getPublicConfig } from "~/providers/ConfigProvider"
import { PossibleGrowthBookExperiment } from "~/providers/GrowthBookProvider"
import { backendRequest } from "~/services/backend.server"
import { contentfulRequest } from "~/services/contentful.server"
import { type getCustomerAccountDataPromise } from "~/services/customerAccounts.server"
import { getReferralProgramConfig } from "~/services/referralProgramConfig.server"

const CAPSULES: {
  [contentfulType: string]: ComponentType<
    CapsuleProps & {
      capsule: any
      activityTypeMap?: Record<string, WithActivityTypeFragment>
      activityPriceMap?: Record<string, number | null>
      referralData?: ReferralCapsuleData
    }
  >
} = {
  CapsuleActivityType: ActivityType,
  CapsuleHero: Hero,
  CapsuleMultiImage: MultiImage,
  CapsuleText: Text,
  CapsuleAccordionGroup: lazy(
    () => import("~/components/capsules/AccordionGroup")
  ),
  CapsuleActivityCard: lazy(() => import("~/components/capsules/ActivityCard")),
  CapsuleActivityCardGroup: lazy(
    () => import("~/components/capsules/ActivityCardGroup")
  ),
  CapsuleButton: lazy(() => import("~/components/capsules/Button")),
  CapsuleEmbed: lazy(() => import("~/components/capsules/Embed")),
  CapsuleKlaviyoForm: lazy(() => import("~/components/capsules/KlaviyoForm")),
  CapsuleMerchingRow: lazy(() => import("~/components/capsules/Merching")),
  CapsuleModule: lazy(() => import("~/components/capsules/Module")),
  CapsuleReferral: lazy(() => import("~/components/capsules/Referral")),
  CapsuleScript: lazy(() => import("~/components/capsules/Script")),
  CapsuleUgcCard: lazy(() => import("~/components/capsules/UgcCard")),
  CapsuleUgcCardGroup: lazy(() => import("~/components/capsules/UgcCardGroup")),
  CapsuleVideo: lazy(() => import("~/components/capsules/Video")),
  LocationInfoCard: lazy(() => import("~/components/capsules/LocationInfoCard"))
}

export const ErrorBoundary = DefaultErrorBoundary

const BACKEND_CACHE_TTL_S = {
  membershipProduct: 3600,
  processingFeeProduct: 3600,
  activityTypes: 3600,
  activityPrices: 1800
} as const

export const loader = async ({ params, request }: LoaderFunctionArgs) => {
  type CustomerAccountData = Awaited<
    ReturnType<typeof getCustomerAccountDataPromise>
  >

  const slug = (params["*"] || "homepage").toLowerCase()
  const slugParts = slug.split("/")
  const locationSlug = slugParts[0]
  const customerAccountDataPromise: Promise<CustomerAccountData> =
    Promise.resolve(null)
  const topBunkMembershipPromise = backendRequest(
    request,
    TopBunkMembershipDocument,
    undefined,
    BACKEND_CACHE_TTL_S.membershipProduct
  )
  const processingFeeProductPromise = backendRequest(
    request,
    ProcessingFeeProductDocument,
    undefined,
    BACKEND_CACHE_TTL_S.processingFeeProduct
  )

  const searchParams = getSearchParams(request)
  const preview = searchParams.has("preview")
  const hasSpecificActivityIds = searchParams.has("activity_ids")
  const stubActivityTypeId = getPublicConfig(request).stubActivityTypeId

  const contentfulDataPromise = contentfulRequest(request, CapsuleDocument, {
    slug,
    path: `/${slug}`,
    preview
  })

  const contentfulData = await contentfulDataPromise
  const redirectEntry = contentfulData?.redirectCollection?.items[0]?.to

  if (redirectEntry) {
    return redirect(redirectEntry)
  }

  const page = contentfulData?.capsulePageCollection?.items[0]

  if (!page) {
    throw json("Page not found", { status: 404 })
  }

  const capsules = page.capsulesCollection?.items ?? []
  type ActivityTypeLocationPair = {
    activityTypeId: string
    locationSlug: string
  }

  const toPair = (
    activityTypeId: string | null | undefined,
    locationSlug: string | null | undefined
  ): ActivityTypeLocationPair | null =>
    activityTypeId && locationSlug ? { activityTypeId, locationSlug } : null

  // Gather all activity-card references (direct cards + cards nested in groups).
  const activityTypeLocationPairs = [
    ...new Map(
      capsules
        .flatMap(item => {
          if (item?.__typename === "CapsuleActivityCard") {
            const activityCardCapsule = item as WithCapsuleActivityCardFragment
            return [
              toPair(
                stubActivityTypeId ??
                  activityCardCapsule.activityType?.activityTypeId,
                activityCardCapsule.locationSlug
              )
            ]
          }

          if (item?.__typename === "CapsuleActivityType") {
            const activityTypeCapsule = item as WithCapsuleActivityTypeFragment
            return [
              toPair(
                stubActivityTypeId ??
                  activityTypeCapsule.activityType?.activityTypeId,
                activityTypeCapsule.location
                  ? resolveLocationSlugAlias(activityTypeCapsule.location)
                  : null
              )
            ]
          }

          if (item?.__typename === "CapsuleActivityCardGroup") {
            const activityCardGroupCapsule =
              item as WithCapsuleActivityCardGroupFragment

            return (
              activityCardGroupCapsule.activityCardsCollection?.items?.map(
                activityCardItem =>
                  toPair(
                    stubActivityTypeId ??
                      activityCardItem?.activityType?.activityTypeId,
                    activityCardItem?.locationSlug
                  )
              ) ?? []
            )
          }

          return []
        })
        .filter((pair): pair is ActivityTypeLocationPair => Boolean(pair))
        .map(pair => [`${pair.activityTypeId}:${pair.locationSlug}`, pair])
    ).values()
  ]

  // Fetch activity types once per ID, even if used in multiple locations.
  const activityTypeIds = [
    ...new Set(activityTypeLocationPairs.map(pair => pair.activityTypeId))
  ]

  const activityTypesPromise =
    activityTypeIds.length > 0
      ? backendRequest(
          request,
          ActivityTypesDocument,
          { filters: { ids: activityTypeIds } },
          BACKEND_CACHE_TTL_S.activityTypes
        )
      : Promise.resolve(null)

  const activityPricesPromise =
    activityTypeLocationPairs.length > 0
      ? backendRequest(
          request,
          ActivityCardPricesDocument,
          { inputs: activityTypeLocationPairs },
          BACKEND_CACHE_TTL_S.activityPrices
        )
      : Promise.resolve(null)

  const shouldFetchLocationData =
    Boolean(locationSlug) && locationSlug !== "homepage"
  const locationSlugForData = resolveLocationSlugAlias(locationSlug)

  const locationDataPromise = shouldFetchLocationData
    ? backendRequest(request, LocationBySlugDocument, {
        slug: locationSlugForData
      }).catch(() => null)
    : Promise.resolve(null)
  const [
    customerAccountData,
    topBunkMembershipData,
    processingFeeProductData,
    activityTypesData,
    activityPricesData,
    locationData
  ] = await Promise.all([
    customerAccountDataPromise,
    topBunkMembershipPromise,
    processingFeeProductPromise,
    activityTypesPromise,
    activityPricesPromise,
    locationDataPromise
  ])

  // Build `activityTypeId:locationSlug -> dollars` lookup for capsule rendering.
  const activityPriceMap: Record<string, number | null> =
    activityPricesData?.activityCardPrices
      ? Object.fromEntries(
          activityPricesData.activityCardPrices
            .filter((entry): entry is NonNullable<typeof entry> =>
              Boolean(entry)
            )
            .map(entry => [
              `${entry.activityTypeId}:${entry.locationSlug}`,
              entry.bestPrice != null ? entry.bestPrice / 100 : null
            ])
        )
      : {}

  // Build `activityTypeId -> activity type` lookup for fast access in capsules.
  const activityTypeMap: Record<string, WithActivityTypeFragment> =
    activityTypesData?.activityTypes?.edges
      ? Object.fromEntries(
          activityTypesData.activityTypes.edges
            .map(edge => edge?.node)
            .filter((node): node is WithActivityTypeFragment => !!node?.id)
            .map(node => [node.id, node])
        )
      : {}

  // Redirect to canonical URL
  if (slug != page.slug) {
    return redirect(`/${page.slug}`)
  }

  const membershipProduct = topBunkMembershipData?.topBunkMembershipProduct

  const shopBanner = contentfulData?.shopBanners?.items[0]

  const customer = customerAccountData?.customer

  const customerMembershipVersion = customer?.membershipVersion?.value

  const currentTopBunkMembershipDiscounts =
    topBunkMembershipData.currentTopBunkMembershipDiscounts

  const topBunkMembershipDiscount =
    currentTopBunkMembershipDiscounts && customerMembershipVersion
      ? currentTopBunkMembershipDiscounts.find(
          ({ title }) => title && title.includes(customerMembershipVersion)
        )
      : currentTopBunkMembershipDiscounts &&
        currentTopBunkMembershipDiscounts.at(-1)

  const processingFeeProductPrice =
    processingFeeProductData.processingFeeProduct?.variants.at(0)?.price ?? 0

  // A non-empty referral_code query param means a referred friend arrived at
  // this capsule via a share link -> the capsule renders the friend landing
  // instead of the advocate panel.
  const referralCode = searchParams.get("referral_code")?.trim()

  // Cheap synchronous defaults (no network call) — always available for the
  // Referral capsule, same as every other CAPSULES entry gets its shared data.
  //
  // Nothing per-visitor belongs here: this response is served with
  // `public, s-maxage` (see headersForCaching below), so a shared cache would
  // hand one visitor's saved state to the next. The referral code is safe
  // because it comes from the query string, which is part of the cache key;
  // cookie- and session-derived state loads from /api/referral-state instead.
  const referralData: ReferralCapsuleData = {
    copy: getReferralProgramConfig().copy,
    friend: referralCode ? { referralCode } : null
  }

  return json(
    {
      page,
      shopBanner,
      customer,
      membershipProduct,
      topBunkMembershipDiscount,
      processingFeeProductPrice,
      hasSpecificActivityIds,
      activityTypeMap,
      activityPriceMap,
      referralData,
      location: locationData?.locationBySlug ?? null
    },
    {
      headers: headersForCaching(preview)
    }
  )
}

export const headers = headersFromLoader

export const meta: MetaFunction<typeof loader> = ({
  data,
  matches,
  location
}) => {
  if (!data) return buildMetaTags(matches, location)

  const imageUrl = data.page.image?.url
    ? contentfulImageUrl(data.page.image.url, {
        width: 1200,
        height: 627
      })
    : null

  // Get LCP image for preloading
  const lcpCapsule = data.page.capsulesCollection?.items
    .filter(item => (item as any)?.isMainContent === true)
    .find(item => item?.__typename === "CapsuleMultiImage")

  const lcpImage = lcpCapsule?.itemsCollection?.items[0]

  // Determine the sizes that will be used for the LCP image
  // For MultiImage, calculate based on visible images per line
  const visibleImagesMobile = (lcpCapsule as any)?.visibleImagesMobile || 1
  const visibleImagesDesktop = (lcpCapsule as any)?.visibleImagesDesktop || 1

  const lcpSizeMobile = `${100 / visibleImagesMobile}vw`
  const lcpSizeDesktop = `${100 / visibleImagesDesktop}vw`

  // Determine container constraints for the LCP image
  const isFullWidth = (lcpCapsule as any)?.fullWidth || false
  const cardMaxWidth = (lcpCapsule as any)?.cardMaxWidth
  let containerType: keyof typeof CONTAINER_SIZES | undefined

  if (!isFullWidth) {
    if (!cardMaxWidth) {
      containerType = "camp-container"
    } else if (cardMaxWidth === "75%") {
      containerType = "camp-container"
    } else if (cardMaxWidth === "50%" || cardMaxWidth === "25%") {
      containerType = "camp-container-mobile"
    } else {
      containerType = "camp-container"
    }
  }

  const lcpPreloads: Array<{
    rel: string
    as: string
    href: string
    imagesrcset?: string
    imagesizes?: string
    type?: string
    media?: string
  }> = []

  if (lcpImage?.image?.url || lcpImage?.imageMobile?.url) {
    const mobileImageUrl = lcpImage.imageMobile?.url
    const desktopImageUrl = lcpImage.image?.url

    // Preload mobile image if it exists and is different from desktop
    if (mobileImageUrl && mobileImageUrl !== desktopImageUrl) {
      // Calculate exact display size for mobile viewport
      const mobileDisplaySize = calculateExactDisplaySize(
        lcpSizeMobile,
        375,
        containerType,
        isFullWidth ? 0 : 16 // Add padding if not full width
      )

      // Calculate optimal width accounting for DPR
      const mobileWidth = calculateOptimalImageWidth(
        `${mobileDisplaySize}px`,
        375,
        3,
        true
      )
      const format = getPreloadFormat()

      lcpPreloads.push({
        rel: "preload",
        as: "image",
        href: contentfulImageUrl(mobileImageUrl, {
          width: mobileWidth,
          format,
          quality: 75
        }),
        type: `image/${format}`,
        media: "(max-width: 735px)"
      })
    }

    // Preload desktop image
    const imageToPreload = desktopImageUrl || mobileImageUrl
    if (imageToPreload) {
      // When no separate mobile image exists, preload for both mobile and desktop
      if (!mobileImageUrl || mobileImageUrl === desktopImageUrl) {
        // Calculate exact display size for mobile viewport
        const mobileDisplaySize = calculateExactDisplaySize(
          lcpSizeMobile,
          375,
          containerType,
          isFullWidth ? 0 : 16 // Add padding if not full width
        )

        const mobileWidth = calculateOptimalImageWidth(
          `${mobileDisplaySize}px`,
          375,
          3,
          true // Use mobile srcset widths (caps at 750px)
        )
        const format = getPreloadFormat()

        lcpPreloads.push({
          rel: "preload",
          as: "image",
          href: contentfulImageUrl(imageToPreload, {
            width: mobileWidth,
            format,
            quality: 75
          }),
          type: `image/${format}`,
          media: "(max-width: 735px)"
        })
      }

      // Always preload desktop size for desktop viewport
      const desktopDisplaySize = calculateExactDisplaySize(
        lcpSizeDesktop,
        1440,
        containerType,
        0 // No padding on desktop
      )

      const desktopWidth = calculateOptimalImageWidth(
        `${desktopDisplaySize}px`,
        1440,
        1,
        false
      )
      const format = getPreloadFormat()

      lcpPreloads.push({
        rel: "preload",
        as: "image",
        href: contentfulImageUrl(imageToPreload, {
          width: desktopWidth,
          format,
          quality: 75
        }),
        type: `image/${format}`,
        media: "(min-width: 736px)"
      })
    }
  }

  // Don't pass multiImageUrls to buildMetaTags since we're handling LCP preloads ourselves
  const multiImageUrls: never[] = []

  const videoThumbnailUrls =
    data.page.capsulesCollection?.items
      .map(item =>
        item?.__typename === "CapsuleVideo" && item.isMainContent
          ? item.previewImage?.url
            ? contentfulImageUrl(item.previewImage.url, {
                quality: 50,
                width: 750,
                format: "webp"
              })
            : `https://image.mux.com/${item.file?.playbackId}/thumbnail.webp?width=750&time=0`
          : null
      )
      .filter((url): url is string => url !== null) ?? []

  return [
    ...buildMetaTags(matches, location, {
      title: data.page.name,
      description: data.page.blurb,
      imageUrl,
      hidden: (data.page.hidden ?? false) || data.hasSpecificActivityIds,
      multiImageUrls,
      videoThumbnailUrls,
      noCanonical: data.hasSpecificActivityIds
    }),
    ...lcpPreloads
  ]
}

/** Matches `Section` / Contentful enum casing for border styles. */
type SectionBorderStyle = "Wave" | "Wave-2" | "Arc"

type SectionStyle = {
  backgroundColor?: string
  backgroundImageUrl?: string
  topBorderStyle?: SectionBorderStyle
  bottomBorderStyle?: SectionBorderStyle
  removeBottomMargin?: boolean
}

function normalizeSectionBorderStyle(
  raw: string | undefined | null
): SectionBorderStyle | undefined {
  if (!raw) return undefined
  const key = String(raw).toLowerCase()
  if (key === "wave") return "Wave"
  if (key === "wave-2" || key === "wave2") return "Wave-2"
  if (key === "arc") return "Arc"
  return undefined
}

// Wave/Wave-2 SVGs are w-full of the section, so reservation is % of group
// width. Arc's SVG is 140vw wide regardless of group width, so reservation
// must be expressed in vw to match the SVG's real rendered height.
const SECTION_TOP_BORDER_RESERVATION: Record<SectionBorderStyle, string> = {
  Wave: `${(28 / 1440) * 100}%`,
  "Wave-2": `${(58 / 1440) * 100}%`,
  Arc: `${(140 * 28) / 1440}vw`
}

const NEXT_TOP_BORDER_PADDING_EXTRA_PERCENT = 1

/** Reserve space below the previous group equal to the next section's top-border height. */
function paddingBottomBeforeNextTopBorder(
  nextTopBorderStyle: string | undefined
): string | undefined {
  const normalizedBorderStyle = normalizeSectionBorderStyle(nextTopBorderStyle)
  if (!normalizedBorderStyle) return undefined

  const reservation = SECTION_TOP_BORDER_RESERVATION[normalizedBorderStyle]

  // +1% for horizontal carousel scrollbar / overflow chrome
  return `calc(${reservation} + ${NEXT_TOP_BORDER_PADDING_EXTRA_PERCENT}%)`
}

type GroupedCapsule = {
  item: any
  originalIndex: number
  visibleIndex: number
}

type CapsuleRenderGroup = {
  key: string
  style?: SectionStyle
  items: GroupedCapsule[]
}

const CAPSULE_TYPES_WITH_INTENTIONAL_NEXT_BORDER_OVERLAP = new Set([
  "CapsuleHero"
])

const toSectionStyle = (capsuleSection: any): SectionStyle => ({
  backgroundColor: capsuleSection.backgroundColor ?? undefined,
  backgroundImageUrl: capsuleSection.backgroundImage?.url ?? undefined,
  topBorderStyle: normalizeSectionBorderStyle(capsuleSection.topBorderStyle),
  bottomBorderStyle: normalizeSectionBorderStyle(
    capsuleSection.bottomBorderStyle
  ),
  removeBottomMargin: capsuleSection.removeBottomMargin ?? undefined
})

const groupCapsulesBySection = (capsules: any[]): CapsuleRenderGroup[] => {
  const groups: CapsuleRenderGroup[] = []
  let activeSection: any | null = null
  let currentItems: GroupedCapsule[] = []
  let sectionIndex = 0
  let visibleIndex = 0

  const flushGroup = () => {
    if (currentItems.length === 0) return

    const groupKey = activeSection?.sys?.id
      ? `section-${activeSection.sys.id}-${sectionIndex}`
      : `group-${sectionIndex}`

    groups.push({
      key: groupKey,
      style: activeSection ? toSectionStyle(activeSection) : undefined,
      items: currentItems
    })

    sectionIndex += 1
    currentItems = []
  }

  capsules.forEach((item, originalIndex) => {
    if (!item?.__typename) return

    if (item.__typename === "CapsuleSection") {
      flushGroup()
      activeSection = item
      return
    }

    currentItems.push({ item, originalIndex, visibleIndex })
    visibleIndex += 1
  })

  flushGroup()
  return groups
}

const Page: FC = () => {
  usePageTracking("Capsule")

  const {
    page,
    shopBanner,
    customer,
    membershipProduct,
    topBunkMembershipDiscount,
    processingFeeProductPrice,
    activityTypeMap,
    activityPriceMap,
    referralData
  } = useLoaderData<typeof loader>()
  const groupedCapsules = groupCapsulesBySection(
    page.capsulesCollection?.items ?? []
  )

  return (
    <div className="mx-auto flex w-full max-w-camp-container-limit flex-col">
      <div style={{ backgroundColor: page.backgroundColor ?? undefined }}>
        {shopBanner && !page.hideShopBanner && <Banner link={shopBanner} />}

        {!page.removeTopMargin && <div className="mt-12"></div>}

        <h1 className="sr-only">{page.name}</h1>

        <>
          {groupedCapsules.map((group, groupIndex) => {
            const isLastGroup = groupIndex === groupedCapsules.length - 1
            const nextTopBorder =
              groupedCapsules[groupIndex + 1]?.style?.topBorderStyle
            const shouldAllowNextTopBorderOverlap = group.items.some(
              ({ item }) =>
                CAPSULE_TYPES_WITH_INTENTIONAL_NEXT_BORDER_OVERLAP.has(
                  item.__typename
                )
            )
            const reserveSpaceForNextTopBorder = shouldAllowNextTopBorderOverlap
              ? undefined
              : paddingBottomBeforeNextTopBorder(nextTopBorder)

            const groupChildren = group.items.map(
              ({ item, originalIndex, visibleIndex }, itemIndex) => {
                const isLastCapsuleOnPage =
                  isLastGroup && itemIndex === group.items.length - 1
                // Always server-render LCP components (isMainContent: true) and first 6 capsules
                const shouldServerRender =
                  visibleIndex < 6 || (item as any).isMainContent === true
                const CapsuleComponent = shouldServerRender
                  ? CAPSULES[item.__typename]
                  : null

                return (
                  <div
                    key={`${page.sys.id}-${(item as any)?.sys?.id ?? item.__typename}-${originalIndex}`}
                    id={`section-${originalIndex + 1}`}
                    className={classNames({
                      // Always keep end-of-page breathing room on the final
                      // capsule, regardless of section usage or capsule flags.
                      "mb-6 lg:mb-12":
                        !item?.removeBottomMargin || isLastCapsuleOnPage
                    })}
                  >
                    <PossibleGrowthBookExperiment
                      experiment={item.experimentId}
                      variation={item.experimentVariation}
                    >
                      {shouldServerRender && CapsuleComponent ? (
                        <Suspense
                          fallback={
                            <CapsuleLoadingFallback
                              capsuleType={item.__typename}
                              capsule={item}
                            />
                          }
                        >
                          <CapsuleComponent
                            page={page}
                            capsule={item}
                            customer={customer}
                            membershipProduct={membershipProduct}
                            topBunkMembershipDiscount={
                              topBunkMembershipDiscount
                            }
                            processingFeeProductPrice={
                              processingFeeProductPrice
                            }
                            activityTypeMap={activityTypeMap}
                            activityPriceMap={activityPriceMap}
                            referralData={referralData}
                            index={originalIndex}
                            serverRender
                          />
                        </Suspense>
                      ) : (
                        <DynamicCapsule
                          capsuleType={item.__typename}
                          page={page}
                          capsule={item}
                          customer={customer}
                          membershipProduct={membershipProduct}
                          topBunkMembershipDiscount={topBunkMembershipDiscount}
                          processingFeeProductPrice={processingFeeProductPrice}
                          activityTypeMap={activityTypeMap}
                          activityPriceMap={activityPriceMap}
                          referralData={referralData}
                          index={originalIndex}
                        />
                      )}
                    </PossibleGrowthBookExperiment>
                  </div>
                )
              }
            )

            if (!group.style) {
              return (
                <div
                  key={group.key}
                  style={
                    reserveSpaceForNextTopBorder
                      ? {
                          paddingBottom: reserveSpaceForNextTopBorder,
                          backgroundColor: page.backgroundColor ?? undefined
                        }
                      : undefined
                  }
                >
                  {groupChildren}
                </div>
              )
            }

            return (
              <div
                key={group.key}
                className={classNames({
                  // Keep inter-group spacing outside the section, but avoid
                  // doing that on the last group so spacing can stay painted
                  // by the section background.
                  "mb-6 lg:mb-12":
                    !group.style.removeBottomMargin && !isLastGroup
                })}
              >
                <Section
                  backgroundColor={group.style.backgroundColor}
                  backgroundImageUrl={group.style.backgroundImageUrl}
                  topBorderStyle={group.style.topBorderStyle}
                  bottomBorderStyle={group.style.bottomBorderStyle}
                  paddingBottom={reserveSpaceForNextTopBorder}
                >
                  {groupChildren}
                </Section>
              </div>
            )
          })}
        </>
      </div>
    </div>
  )
}

export default Page
